import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { ActivityComposer } from './ActivityComposer';
import { useCurrentUser } from '../lib/useCurrentUser';
import { formatDateTime } from '../lib/orgTimezone';
import { feedKey, type FeedEmail, type FeedEvent, type FeedItem, type FeedNote, type RecordFeedResponse } from '../lib/recordFeed';

/**
 * EntityActivityPanel — the ONE stream on a record (spec 0014, AC-10 / AC-11): comments, emails
 * and lifecycle events together, newest first, with a type filter (All, Comments, Emails, Events),
 * pinned items held at the top, threaded replies under their comment, and the Comment / Email
 * composer at the foot.
 *
 *   GET  /api/activity/entity/:type/:id?include=notes,emails
 *   PUT  /api/activity/pins/:kind/:id            Pin to record / Unpin
 *   PUT  /api/workflow/edit-note/:id · DELETE /api/workflow/delete-note/:id   own comments
 *   POST /api/mail/drafts/:id/retry               a draft Outlook refused
 *   POST /api/tasks                               Create follow-up task
 *
 * Every item has a menu: Copy link, Reply (comments), Open in Outlook (emails), Pin to record,
 * Create follow-up task, Edit and Delete (your own comments). A pushed email is a draft in its
 * author's Outlook; appealstack does not know whether it was sent (one-way, this phase).
 */

type Filter = 'all' | 'note' | 'email' | 'event';
const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'All' }, { value: 'note', label: 'Comments' }, { value: 'email', label: 'Emails' }, { value: 'event', label: 'Events' },
];
const ADMIN_ROLES = new Set(['super_admin', 'admin', 'administrator']);

export interface EntityActivityPanelProps {
  entityType: string;
  entityId: string;
  limit?: number;
}

function errText(err: unknown): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) return String((err.body as { error: unknown }).error);
  return err instanceof Error ? err.message : String(err);
}

const EMAIL_STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral' }> = {
  pushed: { label: 'In Outlook Drafts', variant: 'success' },
  draft: { label: 'Draft saved', variant: 'neutral' },
  failed: { label: 'Not pushed', variant: 'danger' },
  pending: { label: 'Awaiting approval', variant: 'warning' },
  sent: { label: 'Sent', variant: 'info' },
};

export function EntityActivityPanel({ entityType, entityId, limit = 100 }: EntityActivityPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const me = useCurrentUser();
  const [filter, setFilter] = useState<Filter>('all');
  const [replyTo, setReplyTo] = useState<FeedNote | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(null);
  const [editing, setEditing] = useState<FeedNote | null>(null);
  const [editText, setEditText] = useState('');
  const [deleting, setDeleting] = useState<FeedNote | null>(null);
  const [taskFor, setTaskFor] = useState<FeedItem | null>(null);
  const [busy, setBusy] = useState(false);

  const key = feedKey(entityType, entityId);
  const query = useQuery<RecordFeedResponse>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<RecordFeedResponse>(`/api/activity/entity/${entityType}/${entityId}?include=notes,emails&limit=${limit}`, signal),
    enabled: !!entityType && !!entityId,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const items = useMemo(() => (Array.isArray(query.data?.items) ? query.data!.items : []), [query.data]);

  // Replies render under their comment (oldest first), so the stream holds top-level items only.
  const repliesOf = useMemo(() => {
    const m = new Map<string, FeedNote[]>();
    for (const it of items) {
      if (it.kind === 'note' && it.parent_note_id) {
        const list = m.get(it.parent_note_id) ?? [];
        list.push(it);
        m.set(it.parent_note_id, list);
      }
    }
    for (const list of m.values()) list.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    return m;
  }, [items]);
  const topLevel = items.filter((it) => !(it.kind === 'note' && it.parent_note_id && items.some((p) => p.id === it.parent_note_id)));
  const visible = topLevel.filter((it) => filter === 'all' || it.kind === filter);
  const pinned = visible.filter((it) => it.kind !== 'event' && it.pinned_at);
  const stream = visible.filter((it) => !(it.kind !== 'event' && it.pinned_at));

  // Arriving on a copied link (#activity-item-<id>) scrolls to that item once it has loaded.
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || !items.length || typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (hash.startsWith('#activity-item-')) {
      const el = document.getElementById(hash.slice(1));
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
      scrolled.current = true;
    }
  }, [items]);

  async function act(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    try { await fn(); await refresh(); if (done) toast({ variant: 'success', message: done }); return true; } catch (err) {
      toast({ variant: 'error', message: errText(err) });
      return false;
    } finally { setBusy(false); }
  }

  const isMine = (it: FeedNote | FeedEmail) => !!me && it.author_user_id === me.id;
  const canDeleteNote = (n: FeedNote) => !!me && (n.author_user_id === me.id || (!!me.role && ADMIN_ROLES.has(me.role)));

  function copyLink(it: FeedItem) {
    const url = `${window.location.origin}${window.location.pathname}?tab=activity#activity-item-${it.id}`;
    const done = () => toast({ variant: 'success', message: 'Link copied.' });
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, () => toast({ variant: 'info', message: url }));
      else toast({ variant: 'info', message: url });
    } catch { toast({ variant: 'info', message: url }); }
  }

  const menuFor = (it: FeedItem): MenuAction[] => {
    const out: MenuAction[] = [{ label: 'Copy link', run: () => copyLink(it) }];
    if (it.kind === 'note') out.push({ label: 'Reply', run: () => setReplyTo(it) });
    if (it.kind === 'email' && it.provider_web_link) out.push({ label: 'Open in Outlook', href: it.provider_web_link });
    if (it.kind !== 'event') {
      out.push(it.pinned_at
        ? { label: 'Unpin', run: () => act(() => api.put(`/api/activity/pins/${it.kind}/${it.id}`, { pinned: false }), 'Unpinned.') }
        : { label: 'Pin to record', run: () => act(() => api.put(`/api/activity/pins/${it.kind}/${it.id}`, { pinned: true }), 'Pinned to the top of this record.') });
    }
    out.push({ label: 'Create follow-up task', run: () => setTaskFor(it) });
    if (it.kind === 'note' && isMine(it)) out.push({ label: 'Edit', run: () => { setEditing(it); setEditText(it.content); } });
    if (it.kind === 'note' && canDeleteNote(it)) out.push({ label: 'Delete', danger: true, run: () => setDeleting(it) });
    return out;
  };

  const renderItem = (it: FeedItem, nested = false): React.ReactNode => (
    <li key={it.id} id={`activity-item-${it.id}`} data-kind={it.kind} style={{ padding: 'var(--sp-3) 0', borderBottom: nested ? 'none' : '1px solid var(--color-border)', marginLeft: nested ? 'var(--sp-5)' : 0 }}>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {it.kind === 'note' && <NoteBody note={it} />}
          {it.kind === 'email' && <EmailBody email={it} mine={isMine(it)} busy={busy}
            onRetry={() => act(() => api.post(`/api/mail/drafts/${it.id}/retry`, {}), 'In your Outlook Drafts.')}
            onResume={() => setResumeId(it.id)} />}
          {it.kind === 'event' && <EventBody event={it} />}
        </div>
        <ItemMenu label={`More actions for this ${it.kind === 'note' ? 'comment' : it.kind === 'email' ? 'email' : 'event'}`} actions={menuFor(it)} />
      </div>
      {it.kind === 'note' && (repliesOf.get(it.id) ?? []).length > 0 && (
        <ul aria-label="Replies" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {(repliesOf.get(it.id) ?? []).map((r) => renderItem(r, true))}
        </ul>
      )}
    </li>
  );

  return (
    <div data-testid="entity-activity-panel" style={{ display: 'grid', gap: 'var(--sp-3)' }}>
      <div role="group" aria-label="Show" style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button key={f.value} type="button" aria-pressed={filter === f.value} onClick={() => setFilter(f.value)} style={{
            border: '1px solid var(--color-border)', borderRadius: 999, padding: '2px 12px', cursor: 'pointer', fontSize: 'var(--fs-sm)',
            background: filter === f.value ? 'var(--brand-primary-bg)' : 'var(--color-card)', color: filter === f.value ? 'var(--brand-primary)' : 'var(--color-ink-2)',
          }}>{f.label}</button>
        ))}
      </div>

      {query.isLoading && <p style={{ color: 'var(--color-ink-3)' }}>Loading activity…</p>}
      {query.error && (
        <div role="alert" style={{ padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)' }}>
          Failed to load activity. {(query.error as Error).message}
        </div>
      )}

      {pinned.length > 0 && (
        <section aria-label="Pinned" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: '0 var(--sp-3)', background: 'var(--color-bg)' }}>
          <h3 style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Pinned</h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{pinned.map((it) => renderItem(it))}</ul>
        </section>
      )}

      {query.data && stream.length === 0 && pinned.length === 0 && (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          {filter === 'all' ? 'Nothing on this record yet. Add a comment or write an email below.'
            : filter === 'note' ? 'No comments yet.' : filter === 'email' ? 'No emails yet.' : 'No events yet.'}
        </p>
      )}
      {stream.length > 0 && <ul aria-label="Activity" style={{ listStyle: 'none', margin: 0, padding: 0 }}>{stream.map((it) => renderItem(it))}</ul>}

      <ActivityComposer entityType={entityType} entityId={entityId} replyTo={replyTo} onCancelReply={() => setReplyTo(null)}
        resumeDraftId={resumeId} onResumed={() => setResumeId(null)} />

      <Modal open={!!editing} onClose={() => { if (!busy) setEditing(null); }} title="Edit comment" size="sm"
        footer={<>
          <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!editText.trim()} onClick={async () => { if (editing && await act(() => api.put(`/api/workflow/edit-note/${editing.id}`, { content: editText.trim() }), 'Comment updated.')) setEditing(null); }}>Save</Button>
        </>}>
        <textarea aria-label="Edit comment" rows={4} value={editText} onChange={(e) => setEditText(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 'var(--fs-sm)', padding: 'var(--sp-2)', borderRadius: 'var(--r-md)', border: '1px solid var(--color-border)' }} />
      </Modal>

      <Modal open={!!deleting} onClose={() => { if (!busy) setDeleting(null); }} title="Delete comment" size="sm"
        footer={<>
          <Button variant="secondary" onClick={() => setDeleting(null)} disabled={busy}>Cancel</Button>
          <Button variant="danger" loading={busy} onClick={async () => { if (deleting && await act(() => api.delete(`/api/workflow/delete-note/${deleting.id}`), 'Comment deleted.')) setDeleting(null); }}>Delete</Button>
        </>}>
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>This comment will be removed from the record.</p>
      </Modal>

      {taskFor && <FollowUpTaskModal item={taskFor} entityType={entityType} entityId={entityId} onClose={() => setTaskFor(null)} />}
    </div>
  );
}

// ── Item bodies ──────────────────────────────────────────────────────────────

const meta = { fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' } as const;

function NoteBody({ note }: { note: FeedNote }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 'var(--fs-sm)' }}>{note.author_name ?? 'Unknown user'}</strong>
        <span style={meta}>commented · {formatDateTime(note.timestamp)}{note.edited_at ? ' · edited' : ''}</span>
      </div>
      <div style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap', marginTop: 2 }}>{note.content}</div>
    </>
  );
}

function EmailBody({ email, mine, busy, onRetry, onResume }: { email: FeedEmail; mine: boolean; busy: boolean; onRetry: () => void; onResume: () => void }) {
  const status = EMAIL_STATUS[email.status] ?? { label: email.status, variant: 'neutral' as const };
  const verb = email.channel === 'outlook_draft'
    ? (email.status === 'pushed' ? 'pushed a draft to Outlook' : email.status === 'failed' ? 'tried to push a draft to Outlook' : 'is writing an email')
    : 'email';
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <span aria-hidden="true">✉</span>
        <strong style={{ fontSize: 'var(--fs-sm)' }}>{email.channel === 'outlook_draft' ? (email.author_name ?? 'Someone') : 'System'}</strong>
        <span style={meta}>{verb} · {formatDateTime(email.timestamp)}</span>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>
      <div style={{ fontSize: 'var(--fs-sm)', marginTop: 2 }}>{email.subject ?? <em style={meta}>Subject hidden for your role</em>}</div>
      <div style={meta}>To {email.to.join(', ') || '—'}{email.cc.length ? ` · Cc ${email.cc.join(', ')}` : ''}</div>
      {email.status === 'failed' && email.last_error && <div role="alert" style={{ ...meta, color: 'var(--color-status-red)' }}>{email.last_error}</div>}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-1)' }}>
        {email.provider_web_link && <a href={email.provider_web_link} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--fs-xs)' }}>Open in Outlook</a>}
        {mine && email.status === 'failed' && <Button variant="secondary" size="sm" onClick={onRetry} disabled={busy}>Retry push</Button>}
        {mine && email.status === 'draft' && <Button variant="secondary" size="sm" onClick={onResume}>Resume draft</Button>}
      </div>
    </>
  );
}

function EventBody({ event }: { event: FeedEvent }) {
  const color = event.action === 'create' ? 'var(--color-status-green, #2e7d32)' : event.action === 'delete' ? 'var(--color-status-red)' : 'var(--color-ink-3)';
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'baseline' }}>
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, background: color, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>{event.message}</span>
      <span style={meta}>{formatDateTime(event.timestamp)}</span>
    </div>
  );
}

// ── Item menu ────────────────────────────────────────────────────────────────

interface MenuAction { label: string; run?: () => void; href?: string; danger?: boolean }

function ItemMenu({ label, actions }: { label: string; actions: MenuAction[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 6px', fontSize: 'var(--fs-md)', color: 'var(--color-ink-3)' }}>⋯</button>
      {open && (
        <ul role="menu" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 30, listStyle: 'none', margin: 0, padding: 'var(--sp-1)', minWidth: 200, background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-md, 0 6px 18px rgba(0,0,0,0.18))' }}>
          {actions.map((a) => (
            <li key={a.label} role="none">
              {a.href
                ? <a role="menuitem" href={a.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} style={menuItem}>{a.label}</a>
                : <button type="button" role="menuitem" onClick={() => { setOpen(false); if (a.run) a.run(); }} style={{ ...menuItem, color: a.danger ? 'var(--color-status-red)' : 'var(--color-ink)' }}>{a.label}</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
const menuItem: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: 'var(--sp-2) var(--sp-3)', borderRadius: 6, cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', textDecoration: 'none' };

// ── Follow-up task ───────────────────────────────────────────────────────────

function FollowUpTaskModal({ item, entityType, entityId, onClose }: { item: FeedItem; entityType: string; entityId: string; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const seed = item.kind === 'note' ? item.content : item.kind === 'email' ? (item.subject ?? 'Email') : item.message;
  const [title, setTitle] = useState(`Follow up: ${seed.replace(/\s+/g, ' ').slice(0, 80)}`);
  const [due, setDue] = useState('');
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      const actionUrl = `${window.location.pathname}?tab=activity#activity-item-${item.id}`;
      await api.post('/api/tasks', { title: title.trim(), body: seed.slice(0, 500), entity_type: entityType, entity_id: entityId, due_date: due || null, action_url: actionUrl });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ variant: 'success', message: 'Follow-up task created for you.' });
      onClose();
    } catch (err) {
      toast({ variant: 'error', message: errText(err) });
    } finally { setSaving(false); }
  }
  return (
    <Modal open onClose={() => { if (!saving) onClose(); }} title="Create follow-up task" size="sm"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!title.trim()}>Create task</Button>
      </>}>
      <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
        <label style={{ display: 'grid', gap: 4 }}><span style={meta}>Task</span><Input aria-label="Task title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
        <label style={{ display: 'grid', gap: 4 }}><span style={meta}>Due</span><Input aria-label="Due date" type="date" value={due} onChange={(e) => setDue(e.target.value)} /></label>
        <p style={meta}>The task is assigned to you and links back to this item.</p>
      </div>
    </Modal>
  );
}
